/* eslint-disable max-lines */
import {
	type Country,
	DateObject,
	EventIdentifier,
	FeatureTeamEvent,
	queryClient,
	type TrackingEventType,
	type TrackingEventTypeWEB,
	useAnalytics,
	useCountries,
	useFiatCurrencies,
	useIntl,
	type UserDeliveryAddress2
} from '@capsicum/common';
import { useWalletLedgers } from '@capsicum/features/wallet/shared';
import { useConversionRatesQuery, useMainCurrenciesQuery, useMe, useWalletBalance } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	placeBidIdAtom,
	useAuctionDetailsQueries,
	useBidListQueries,
	useFetchCollectibleAuctionCostsByAuctionId,
	usePostBidQueries
} from '../../data-access';
import { setDeliveryCountryAtom } from '../../data-access/atoms/setDeliveryCountry';
import {
	UseCollectibleEventsQueryKeyBuilder,
	usePostUserDeliveryAddressQueries,
	usePutCollectibleDeliveryByDeliveryId,
	usePutUserDeliveryAddressQueries
} from '../../data-access/queries';
import { useAuctionTimer } from '../../hooks/components/useAuctionCard';
import { type AxiosErrorData, PostBidErrors, PostBidErrorsEnum, type WebSocketPayload } from '../../models';
import { useAuctions } from '../useAuctions';

import { useAuctionStatusUpdateFromWS } from './useAuctionStatusUpdateFromWS';

export const DEFAULT_BID_LIMIT = 4;

export type ConfirmDeliveryDrawerProps = {
	city?: string;
	country?: string;
	id: string;
	street?: string;
	zipCode?: string;
};

export const useAuctionDetails = (
	auctionId: string,
	toasts: {
		onFailedBid: (translationCode: string, errorsObj?: Array<AxiosErrorData> | undefined) => void;
		onPendingBid: () => void;
		onSuccessfulBid: () => void;
	}
) => {
	const { $t } = useIntl();
	const { data: currencies } = useMainCurrenciesQuery();
	const [refreshing, setRefreshing] = useState<boolean>(false);
	const [pendingBid, setPendingBid] = useState<boolean>(false);
	const { data: fiatCurrencies } = useFiatCurrencies();
	const [userBidAmount, setUserBidAmount] = useState<number>(0);
	const [collectibleCostsLoading, setCollectibleCostsLoading] = useState<boolean>(false);
	const chzCurrencyId = useMemo(() => currencies?.defaultCurrency?.id || '', [currencies]);
	const defaultFiatCurrencyId = useMemo(() => currencies?.altCurrency?.id || '', [currencies]);
	const conversionRatesParams = useMemo(
		() => [{ from: chzCurrencyId, to: defaultFiatCurrencyId }],
		[chzCurrencyId, defaultFiatCurrencyId]
	);
	const euroCurrencyId = useMemo(
		() => fiatCurrencies?.find(currency => currency.currency.shortCode === 'EUR')?.currency.id,
		[fiatCurrencies]
	);
	const conversionRatesEuroParams = useMemo(
		() => [{ from: defaultFiatCurrencyId, to: euroCurrencyId }],
		[defaultFiatCurrencyId, euroCurrencyId]
	);

	const { me } = useMe();
	const { getCountryBy } = useCountries();
	const analytics = useAnalytics();

	const { data: conversionRates } = useConversionRatesQuery(conversionRatesParams);
	const { data: conversionRatesEuro } = useConversionRatesQuery(conversionRatesEuroParams);
	const { data: ledgersData } = useWalletLedgers();
	const [placeBidId, setPlaceBidId] = useAtom(placeBidIdAtom);

	const {
		data: postDeliveryAddressData,
		isPending: isPostDeliveryAddressPending,
		mutateAsync: postDeliveryAddressMutateAsync
	} = usePostUserDeliveryAddressQueries();
	const {
		data: putDeliveryAddressData,
		isPending: isPutDeliveryAddressPending,
		mutateAsync: putDeliveryAddressMutateAsync
	} = usePutUserDeliveryAddressQueries();
	const {
		data: putCollectibleDeliveryByIdData,
		isPending: isPutCollectibleDeliveryByIdPending,
		mutateAsync: putCollectibleDeliveryByIdMutateAsync
	} = usePutCollectibleDeliveryByDeliveryId();
	const { data: chzWalletObj } = useWalletBalance(chzCurrencyId);
	const chzRate = useMemo(() => (conversionRates?.[0]?.rate ? +conversionRates?.[0]?.rate : 0), [conversionRates]);
	const eurRate = useMemo(
		() => (conversionRatesEuro?.[0]?.rate ? +conversionRatesEuro?.[0]?.rate : 0),
		[conversionRatesEuro]
	);

	const currencyNetworkId = useMemo(
		() =>
			ledgersData?.find(
				ledger =>
					ledger.type === 'centralized' &&
					ledger.currency?.type === 'crypto' &&
					ledger.currency?.shortCode === 'CHZ'
			)?.currencyNetwork?.id,
		[ledgersData]
	);

	const [savedCountry, setSavedCountry] = useState(me.address?.countryCode);
	const [selectedCountryIso2, setSelectedCountry] = useAtom(setDeliveryCountryAtom);

	const deliveryAddressForm = useRef<UserDeliveryAddress2>({
		addressLabel: '',
		city: '',
		countryCode: '',
		firstName: '',
		id: '',
		lastName: '',
		mobileNumber: '',
		region: '',
		street: '',
		zipCode: ''
	});

	const setDeliveryAddressForm = (data: UserDeliveryAddress2) => {
		deliveryAddressForm.current = data;
	};

	const {
		data: auctionData,
		isPending: auctionDataPending,
		refetch: refetchAuction
	} = useAuctionDetailsQueries({
		altCurrencyId: currencies?.altCurrency?.id,
		auctionId: auctionId,
		mainCurrencyId: currencies?.defaultCurrency?.id
	});

	const { refetchEvents } = useAuctions({
		// Same parameter as in Auctions.tsx page, allows to skip retriggering the query.
		auctionsPerEvent: 6
	});

	const {
		data: bidList,
		isLoading: isBidListLoading,
		refetch: refetchBidList
	} = useBidListQueries({
		altCurrencyId: currencies?.altCurrency?.id,
		auctionId: auctionId,
		limit: DEFAULT_BID_LIMIT,
		mainCurrencyId: currencies?.defaultCurrency?.id
	});

	// We need to pass "EUR" as currencyShortCode because the API is expecting it.
	// If for the US we'll have to pass "USD" as currencyShortCode
	const {
		data: collectibleCosts,
		error: costsError,
		isLoading: isCollectibleCostsLoading,
		isRefetching: isCollectibleCostsRefetching,
		refetch: collectibleCostsRefetch
	} = useFetchCollectibleAuctionCostsByAuctionId({
		altCurrencyId: currencies?.altCurrency?.id || '',
		auctionId,
		bidAmount: userBidAmount
			? Math.round(userBidAmount * eurRate).toString()
			: Math.round(Number(auctionData?.altCurrency?.minBidAmount) * eurRate).toString(),
		currencyShortCode: 'EUR',
		mainCurrencyId: chzCurrencyId || '',
		shippingCountryCode: selectedCountryIso2 || ''
	});

	const shippingCostData = collectibleCosts;
	const isShippingCostLoading = isCollectibleCostsLoading;

	useEffect(() => {
		if (isCollectibleCostsLoading || isCollectibleCostsRefetching) {
			setCollectibleCostsLoading(true);
		} else {
			setTimeout(() => {
				setCollectibleCostsLoading(false);
			}, 500);
		}
	}, [isCollectibleCostsLoading, isCollectibleCostsRefetching]);

	useEffect(() => {
		if (shippingCostData) {
			setSavedCountry(selectedCountryIso2);
		}
	}, [shippingCostData]);

	useEffect(() => {
		if (!selectedCountryIso2?.length) {
			setSelectedCountry(savedCountry);
		}
		if (costsError) {
			setSelectedCountry(savedCountry);
		}
	}, [selectedCountryIso2, setSelectedCountry, savedCountry, costsError]);

	const { auctionEndDate, currentStatus, setCurrentStatus } = useAuctionTimer(auctionData);

	const getIsOwnBidByUserId = useCallback(
		(userId: string | undefined) => me.externalUserId === userId,
		[me.externalUserId]
	);
	const isOwnBid = getIsOwnBidByUserId(auctionData?.currentBid?.participant?.chilizUserId);
	const isActive = useMemo(
		() =>
			auctionData?.status === 'opened' ||
			auctionData?.status === 'suspended' ||
			auctionData?.status === 'planned',
		[auctionData?.status]
	);
	const hasBids = useMemo(() => !!bidList?.length, [bidList]);

	const winningBid = useMemo(() => bidList?.find(bid => bid.status === 'accepted'), [bidList]);

	const delivery = useMemo(() => winningBid?.delivery || {}, [winningBid?.delivery]);

	const deliveryCountryCode = useMemo(() => delivery?.shippingCountryCode, [delivery?.shippingCountryCode]);

	const deliveryCountry = useMemo(
		() => getCountryBy(deliveryCountryCode, 'iso2') as Country,
		[getCountryBy, deliveryCountryCode]
	);

	const country = useMemo(
		() => getCountryBy(selectedCountryIso2, 'iso2') as Country,
		[getCountryBy, selectedCountryIso2]
	);

	const isShowSuspendedBanner = useMemo(() => auctionData?.status === 'suspended', [auctionData?.status]);

	const isShowWinningBanner = useMemo(
		() =>
			isOwnBid && auctionData?.status === 'finished' && delivery && delivery?.status?.toLowerCase() === 'created',
		[delivery, isOwnBid, auctionData?.status]
	);

	const isBall = useMemo(() => auctionData?.collectible?.type === 'goal_ball', [auctionData?.collectible?.type]);

	const isTimeUp = useMemo(
		() => new DateObject().valueOf() - new DateObject(auctionData?.endAt || '').valueOf() > 0,
		[auctionData?.endAt]
	);

	const bidListStructureData = useMemo(
		() =>
			bidList?.map(bid => {
				return {
					amount: bid.altCurrency?.bidAmount ? +bid.altCurrency.bidAmount : 0,
					amountChz: bid.mainCurrency?.bidAmount ? +bid.mainCurrency.bidAmount : 0,
					date: bid.bidReceivedAt,
					iso2Country: bid.countryCode,
					isOwnBid: bid.participant?.chilizUserId === me.externalUserId,
					mwsUserId: bid.obfuscatedUserId,
					status: bid.status
				};
			}),
		[bidList, me.externalUserId]
	);

	const texts = useMemo(
		() => ({
			aboutThisProduct: $t('rewards.auctions.aboutProduct'),
			closed: $t('rewards.auctions.closed'),
			placeABid: $t('rewards.auctions.placeABid'),
			poweredBy: $t('rewards.auctions.poweredBy')
		}),
		[$t]
	);

	const eventName = useMemo(
		() => auctionData?.collectible?.providerEvent?.event?.name,
		[auctionData?.collectible?.providerEvent?.event?.name]
	);

	const sortedTags = useMemo(
		() => auctionData?.collectible?.tags?.sort((a, b) => b.displayOrder! - a.displayOrder!),
		[auctionData]
	);

	const mainImage = useMemo(
		() => auctionData?.collectible?.providerImages?.[0],
		[auctionData?.collectible?.providerImages]
	);

	const eventsQueryKeys = useMemo(
		() => [
			...UseCollectibleEventsQueryKeyBuilder({
				altCurrencyId: currencies?.altCurrency?.id || '',
				mainCurrencyId: currencies?.defaultCurrency?.id || ''
			})
		],
		[currencies]
	);

	const handleToastStatuses = useCallback(
		(bidStatus: string | undefined, isUsersOwnBid = true, bidId?: string) => {
			switch (bidStatus) {
				case 'created':
					if (isUsersOwnBid) {
						toasts.onPendingBid();
					}
					break;
				case 'accepted':
				case 'validated':
					if (isUsersOwnBid) {
						toasts.onSuccessfulBid();
					}
					setPendingBid(false);
					break;
				case 'rejected':
					if (isUsersOwnBid) {
						toasts.onFailedBid('lateToParty');
						setPendingBid(false);
					}
					break;
				case 'outbidded':
					if (bidId === placeBidId) {
						toasts.onFailedBid('bidWars');
					}
					break;
				default:
					toasts.onFailedBid('unhandledError');
					setPendingBid(false);
			}
		},
		[toasts, placeBidId]
	);

	const handleAuctionUpdate = useCallback(
		({ payload }: WebSocketPayload) => {
			queryClient.invalidateQueries({ queryKey: eventsQueryKeys });

			refetchAuction();
			refetchBidList();
			handleToastStatuses(payload.bidStatus, getIsOwnBidByUserId(payload.chilizUserId), payload.bidId);
		},
		[handleToastStatuses, getIsOwnBidByUserId, eventsQueryKeys, refetchAuction, refetchBidList]
	);

	const handleAuctionStatusUpdate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: eventsQueryKeys });

		refetchAuction();
		refetchEvents();
	}, [eventsQueryKeys, refetchAuction, refetchEvents]);

	useAuctionStatusUpdateFromWS({
		eventsQueryKeys,
		handleAuctionStatusUpdate,
		handleAuctionUpdate
	});

	const selectedCountryTranslated = useMemo(() => (country?.translations?.[0]?.value as string) || '', [country]);
	const minBidFiatAmount = useMemo(
		() => Number(auctionData?.altCurrency?.minBidAmount || 0),
		[auctionData?.altCurrency?.minBidAmount]
	);

	const fiatShortCode = useMemo(
		() =>
			auctionData?.altCurrency?.currency?.shortCode ||
			shippingCostData?.bidAmount?.altCurrency?.currency?.shortCode ||
			'',
		[auctionData?.altCurrency?.currency?.shortCode, shippingCostData?.bidAmount?.altCurrency?.currency?.shortCode]
	);
	const chzShortCode = useMemo(
		() =>
			auctionData?.mainCurrency?.currency?.shortCode ||
			shippingCostData?.bidAmount?.mainCurrency?.currency?.shortCode ||
			'',
		[auctionData?.mainCurrency?.currency?.shortCode, shippingCostData?.bidAmount?.mainCurrency?.currency?.shortCode]
	);
	const fiatCurrencySymbol = useMemo(
		() =>
			auctionData?.altCurrency?.currency?.symbol ||
			shippingCostData?.bidAmount?.altCurrency?.currency?.symbol ||
			'',
		[auctionData?.altCurrency?.currency?.symbol, shippingCostData?.bidAmount?.altCurrency?.currency?.symbol]
	);
	const shippingFiatAmount = useMemo(
		() => Number(shippingCostData?.estimatedShippingCost?.altCurrency?.amount || 0),
		[shippingCostData?.estimatedShippingCost?.altCurrency?.amount]
	);
	const currentFiatBid = useMemo(
		() => Number(auctionData?.currentBid?.altCurrency?.bidAmount || 0),
		[auctionData?.currentBid?.altCurrency?.bidAmount]
	);
	const currentChzBid = useMemo(
		() => Number(auctionData?.currentBid?.mainCurrency?.bidAmount || 0),
		[auctionData?.currentBid?.mainCurrency?.bidAmount]
	);

	const { data: bidPayload, error: postBidErrors, isPending: isPostBidPending, mutateAsync } = usePostBidQueries();

	const submit = useCallback(async () => {
		if (pendingBid) {
			return; // prevent double submission while a bid is pending
		}
		setPendingBid(true);
		try {
			const res = await mutateAsync({
				altCurrencyId: defaultFiatCurrencyId!,
				auctionId,
				bidAmount: (parseFloat(collectibleCosts?.bidAmount?.price as string) || 0).toString(),
				bidCurrencyId: euroCurrencyId!,
				currencyNetworkId: currencyNetworkId!,
				mainCurrencyId: chzCurrencyId || '',
				shippingCountryCode: selectedCountryIso2!
			});

			if (res.data) {
				setPlaceBidId(res?.data?.id);
			}
		} catch (e) {
			setPendingBid(false);
		}
	}, [
		mutateAsync,
		auctionId,
		currencyNetworkId,
		defaultFiatCurrencyId,
		selectedCountryIso2,
		setPlaceBidId,
		collectibleCosts,
		pendingBid
	]);

	const handleErrorMessages = useCallback(
		(errorCode: string, errorsObj?: Array<AxiosErrorData> | undefined) => {
			const error = PostBidErrorsEnum[errorCode as keyof typeof PostBidErrorsEnum];

			if (error) {
				toasts.onFailedBid(error);
			} else {
				toasts.onFailedBid('', errorsObj);
			}
		},
		[toasts]
	);

	useEffect(() => {
		if (bidPayload?.data.status) {
			handleToastStatuses(bidPayload?.data.status);
		} else if (postBidErrors) {
			handleErrorMessages(
				postBidErrors?.response?.data?.errors?.[0]?.code || '',
				postBidErrors?.response?.data?.errors as unknown as Array<AxiosErrorData>
			);
			setPendingBid(false);
		}
	}, [bidPayload, postBidErrors, handleToastStatuses, handleErrorMessages]);

	useEffect(() => {
		refetchBidList();
	}, [putCollectibleDeliveryByIdData]);

	const getBidErrorMessage = useCallback(
		(errorCode: string | undefined) => {
			switch (errorCode) {
				case PostBidErrors.BID_WARS:
					return {
						message: $t('rewards.toast.error.bidWars.message'),
						title: $t('rewards.toast.error.bidWars.title')
					};
				case PostBidErrors.NOT_ALLOWED_TO_PLACE_BID:
					return {
						message: $t('rewards.toast.error.notAllowedToPlaceBid.message'),
						title: $t('rewards.toast.error.notAllowedToPlaceBid.title')
					};
				case PostBidErrors.LATE_TO_PARTY:
					return {
						message: $t('rewards.toast.error.lateToParty.message'),
						title: $t('rewards.toast.error.lateToParty.title')
					};
				case PostBidErrors.COUNTRY_NOT_SUPPORTED:
					return {
						message: $t('rewards.toast.error.countryNotSupported.message'),
						title: $t('rewards.toast.error.countryNotSupported.title')
					};
				default:
					return {
						message: $t('rewards.toast.error.unhandledError.message'),
						title: $t('rewards.toast.error.unhandledError.title')
					};
			}
		},
		[$t]
	);

	const setDeliveryAddressData = useCallback(
		(data: UserDeliveryAddress2) => {
			const { addressLabel, city, countryCode, firstName, id, lastName, mobileNumber, region, street, zipCode } =
				data;

			const country = getCountryBy(countryCode, 'iso2')?.name;

			setDeliveryAddressForm({
				addressLabel,
				city,
				countryCode,
				firstName,
				id,
				lastName,
				mobileNumber,
				region,
				street,
				zipCode
			});

			return {
				addressLabel,
				city,
				country,
				id,
				street,
				zipCode
			};
		},
		[getCountryBy]
	);

	const onRefresh = useCallback(async () => {
		setRefreshing(true);
		await refetchAuction();
		setRefreshing(false);
	}, [refetchAuction]);

	const getNumber = useCallback((value: number | string | undefined) => {
		return typeof value === 'string' ? +value : 0;
	}, []);

	useEffect(() => {
		if (userBidAmount) {
			const refetchCosts = async () => {
				await collectibleCostsRefetch();
			};

			refetchCosts();
		}
	}, [userBidAmount]);

	const isDataLoading =
		auctionDataPending ||
		isBidListLoading ||
		isShippingCostLoading ||
		isCollectibleCostsLoading ||
		isPostDeliveryAddressPending ||
		isPutDeliveryAddressPending ||
		isPutCollectibleDeliveryByIdPending ||
		isPostBidPending;

	const isSkeletonLoading =
		auctionDataPending || isBidListLoading || isShippingCostLoading || isCollectibleCostsLoading;

	const sendAnalyticsEvent = useCallback(
		(eventType: TrackingEventType | TrackingEventTypeWEB, params: Record<string, unknown>) => {
			analytics.sendEvent(eventType, EventIdentifier.ButtonClicked, {
				action: 'auction_click_bid',
				currency: fiatCurrencySymbol,
				feature_team: FeatureTeamEvent.RFT,
				item_type: isBall ? 'Goal Ball' : 'Shirt',
				partner_id: auctionData?.collectible?.team?.partnerId,
				player_name: auctionData?.collectible?.name,
				...params
			});
		},
		[auctionData, isBall, fiatCurrencySymbol]
	);

	return {
		auctionData,
		auctionEndDate,
		bidList,
		bidListStructureData,
		chzCurrencyId,
		chzRate,
		chzShortCode,
		chzWalletObj,
		collectibleCosts,
		collectibleCostsLoading,
		collectibleCostsRefetch,
		conversionRates,
		conversionRatesParams,
		country,
		currencyNetworkId,
		currentChzBid,
		currentFiatBid,
		currentStatus,
		defaultFiatCurrencyId,
		deliveryAddressForm,
		deliveryCountry,
		eventName,
		fiatCurrencySymbol,
		fiatShortCode,
		getBidErrorMessage,
		getNumber,
		hasBids,
		isActive,
		isBall,
		isDataLoading,
		isOwnBid,
		isShowSuspendedBanner,
		isShowWinningBanner,
		isSkeletonLoading,
		isTimeUp,
		mainImage,
		minBidFiatAmount,
		onRefresh,
		pendingBid,
		postDeliveryAddressData,
		postDeliveryAddressMutateAsync,
		putCollectibleDeliveryByIdData,
		putCollectibleDeliveryByIdMutateAsync,
		putDeliveryAddressData,
		putDeliveryAddressMutateAsync,
		refetchAuction,
		refetchBidList,
		refreshing,
		selectedCountryIso2,
		selectedCountryTranslated,
		sendAnalyticsEvent,
		setCurrentStatus,
		setDeliveryAddressData,
		setDeliveryAddressForm,
		setSelectedCountry,
		setUserBidAmount,
		shippingCostData,
		shippingFiatAmount,
		sortedTags,
		submit,
		texts,
		useFetchCollectibleAuctionCostsByAuctionId,
		userBidAmount,
		winningBid
	};
};
/* eslint-enable max-lines */
